import React, { useState, useMemo } from 'react';
import { Activity, Zap, ChevronRight, Search, X } from 'lucide-react';
import { safeAsync, sp, Badge, emptyMsg, btnPrimary, neuShadow, neuShadowInset, neuShadowSm, cardWrap, inp } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

export default function AutomationTab({ automationRules, automationLog, canCreate, seeding, setSeeding, loadExtended, onContactClick }) {
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [searchRules, setSearchRules] = useState('');
  const [searchLogs, setSearchLogs] = useState('');

  // Trigger colors
  const triggerColors = {
    contact_created: '#62BB46',
    stage_changed: '#2E8B98',
    lead_scored: '#256F7A',
    scheduled_check: '#F26522'
  };

  // Log result colors
  const resultColors = {
    success: '#62BB46',
    partial: '#F26522',
    failed: '#DC3545'
  };

  // ===== SECTION 1: AUTOMATION RULES =====

  // Group rules by trigger_event
  const groupedRules = useMemo(() => {
    let rules = automationRules;
    if (searchRules.trim()) {
      const q = searchRules.toLowerCase();
      rules = rules.filter(r =>
        r.name.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
      );
    }

    const groups = {
      contact_created: [],
      stage_changed: [],
      lead_scored: [],
      scheduled_check: []
    };

    rules.forEach(rule => {
      if (groups[rule.trigger_event]) {
        groups[rule.trigger_event].push(rule);
      }
    });

    return groups;
  }, [automationRules, searchRules]);

  const triggerEventLabels = {
    contact_created: 'Contact Created',
    stage_changed: 'Stage Changed',
    lead_scored: 'Lead Scored',
    scheduled_check: 'Scheduled Check'
  };

  const toggleRuleGroup = (trigger) => {
    const newSet = new Set(expandedRules);
    if (newSet.has(trigger)) {
      newSet.delete(trigger);
    } else {
      newSet.add(trigger);
    }
    setExpandedRules(newSet);
  };

  const toggleRuleItem = (ruleId) => {
    const newSet = new Set(expandedRules);
    if (newSet.has(`rule_${ruleId}`)) {
      newSet.delete(`rule_${ruleId}`);
    } else {
      newSet.add(`rule_${ruleId}`);
    }
    setExpandedRules(newSet);
  };

  const expandAllRules = () => {
    const allKeys = new Set([
      'contact_created', 'stage_changed', 'lead_scored', 'scheduled_check',
      ...automationRules.map(r => `rule_${r.id}`)
    ]);
    setExpandedRules(allKeys);
  };

  const collapseAllRules = () => {
    setExpandedRules(new Set());
  };

  // Auto-expand all groups when search is active
  const shouldAutoExpandRules = searchRules.trim().length > 0;
  const effectiveExpandedRules = shouldAutoExpandRules
    ? new Set([...expandedRules, 'contact_created', 'stage_changed', 'lead_scored', 'scheduled_check'])
    : expandedRules;

  // ===== SECTION 2: EXECUTION LOG =====

  // Group logs by result
  const groupedLogs = useMemo(() => {
    let logs = automationLog;
    if (searchLogs.trim()) {
      const q = searchLogs.toLowerCase();
      logs = logs.filter(l =>
        matchesNameQuery([l.rule_name, l.first_name, l.last_name], q)
      );
    }

    const groups = {
      success: [],
      partial: [],
      failed: []
    };

    logs.forEach(log => {
      if (groups[log.result]) {
        groups[log.result].push(log);
      }
    });

    return groups;
  }, [automationLog, searchLogs]);

  const toggleLogGroup = (result) => {
    const newSet = new Set(expandedLogs);
    if (newSet.has(result)) {
      newSet.delete(result);
    } else {
      newSet.add(result);
    }
    setExpandedLogs(newSet);
  };

  const expandAllLogs = () => {
    setExpandedLogs(new Set(['success', 'partial', 'failed']));
  };

  const collapseAllLogs = () => {
    setExpandedLogs(new Set());
  };

  // Auto-expand all groups when search is active
  const shouldAutoExpandLogs = searchLogs.trim().length > 0;
  const effectiveExpandedLogs = shouldAutoExpandLogs
    ? new Set(['success', 'partial', 'failed'])
    : expandedLogs;

  // ===== RENDER =====

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} color="#62BB46" /> Workflow Automation
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCreate && automationRules.length === 0 && (
            <button
              onClick={safeAsync(async () => {
                setSeeding(true);
                await window.zignagenixAPI.seedDefaultAutomation();
                await loadExtended();
                setSeeding(false);
              })}
              disabled={seeding}
              style={{ ...btnPrimary, background: 'linear-gradient(145deg, #34d399, #4FA038)' }}>
              <Zap size={13} /> Seed Automation Rules
            </button>
          )}
        </div>
      </div>

      {/* ========== SECTION 1: AUTOMATION RULES ========== */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: 0 }}>Automation Rules</h4>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={expandAllRules}
              style={{
                boxShadow: neuShadowSm,
                padding: '6px 10px',
                borderRadius: 6,
                border: 'none',
                background: '#FFFFFF',
                fontSize: 11,
                fontWeight: 600,
                color: '#0369a1',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#e0f2fe'}
              onMouseOut={(e) => e.target.style.background = '#FFFFFF'}
            >
              Expand All
            </button>
            <button
              onClick={collapseAllRules}
              style={{
                boxShadow: neuShadowSm,
                padding: '6px 10px',
                borderRadius: 6,
                border: 'none',
                background: '#FFFFFF',
                fontSize: 11,
                fontWeight: 600,
                color: '#6C757D',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
              onMouseOut={(e) => e.target.style.background = '#FFFFFF'}
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Search Rules */}
        <div style={{ marginBottom: 12, position: 'relative' }}>
          <input
            type="text"
            placeholder="Search rules by name or description..."
            value={searchRules}
            onChange={(e) => setSearchRules(e.target.value)}
            style={{
              ...inp,
              width: '100%',
              paddingRight: 32
            }}
          />
          {searchRules && (
            <button
              onClick={() => setSearchRules('')}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: '#6C757D'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {automationRules.length === 0 ? (
          <div style={emptyMsg}>No automation rules configured. Seed defaults to get started.</div>
        ) : Object.values(groupedRules).every(arr => arr.length === 0) && searchRules ? (
          <div style={emptyMsg}>No rules match your search.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {Object.entries(groupedRules).map(([trigger, rules]) => {
              if (rules.length === 0) return null;

              const isExpanded = effectiveExpandedRules.has(trigger);
              const color = triggerColors[trigger] || '#6C757D';

              return (
                <div key={trigger} style={{ marginBottom: 8 }}>
                  {/* Group Header */}
                  <div
                    onClick={() => toggleRuleGroup(trigger)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 14px',
                      background: '#FFFFFF',
                      borderRadius: 10,
                      cursor: 'pointer',
                      borderLeft: `4px solid ${color}`,
                      boxShadow: neuShadowSm,
                      transition: 'all 0.2s',
                      userSelect: 'none'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#F8F9FA'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#FFFFFF'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <ChevronRight
                        size={18}
                        color={color}
                        style={{
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
                        {triggerEventLabels[trigger]}
                      </span>
                      <Badge text={rules.length} color={color} />
                    </div>
                  </div>

                  {/* Group Items */}
                  <div
                    style={{
                      maxHeight: isExpanded ? 'auto' : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease-in-out',
                      marginTop: isExpanded ? 8 : 0
                    }}
                  >
                    {rules.map(rule => {
                      const conditions = sp(rule.trigger_conditions, {});
                      const actions = sp(rule.actions, []);
                      const isRuleExpanded = effectiveExpandedRules.has(`rule_${rule.id}`);

                      return (
                        <div
                          key={rule.id}
                          style={{
                            marginBottom: 8,
                            borderRadius: 8,
                            overflow: 'hidden',
                            boxShadow: neuShadowSm
                          }}
                        >
                          {/* Rule Header */}
                          <div
                            onClick={() => toggleRuleItem(rule.id)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 12px',
                              background: '#F8F9FA',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              userSelect: 'none'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#e9ecef'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#F8F9FA'}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ChevronRight
                                size={16}
                                color="#6C757D"
                                style={{
                                  transition: 'transform 0.2s',
                                  transform: isRuleExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                                }}
                              />
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', flex: 1 }}>
                                {rule.name}
                              </span>
                              {rule.priority > 7 && <Badge text="HIGH" color="#DC3545" />}
                            </div>
                            <Badge text={rule.is_active ? 'Active' : 'Paused'} color={rule.is_active ? '#62BB46' : '#F26522'} />
                          </div>

                          {/* Rule Content */}
                          <div
                            style={{
                              maxHeight: isRuleExpanded ? 'auto' : 0,
                              overflow: 'hidden',
                              transition: 'max-height 0.3s ease-in-out',
                              background: '#FFFFFF'
                            }}
                          >
                            <div style={{ padding: '12px', borderTop: '1px solid #e9ecef' }}>
                              {rule.description && (
                                <p style={{ fontSize: 11, color: '#6C757D', margin: '0 0 10px', fontStyle: 'italic' }}>
                                  {rule.description}
                                </p>
                              )}

                              {/* Conditions & Actions Row */}
                              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                {/* Conditions */}
                                <div style={{ flex: 1, background: '#e3f0fa', borderRadius: 8, padding: 9, boxShadow: neuShadowInset }}>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: '#0369a1', marginBottom: 4, textTransform: 'uppercase' }}>
                                    Conditions
                                  </div>
                                  {Object.keys(conditions).length === 0 ? (
                                    <div style={{ fontSize: 10, color: '#6C757D' }}>All events (no filter)</div>
                                  ) : (
                                    Object.entries(conditions).map(([k, v]) => (
                                      <div key={k} style={{ fontSize: 10, color: '#212529', marginBottom: 2 }}>
                                        <strong>{k.replace(/_/g, ' ')}:</strong> {String(v)}
                                      </div>
                                    ))
                                  )}
                                </div>

                                {/* Actions */}
                                <div style={{ flex: 1, background: '#e8f5e9', borderRadius: 8, padding: 9, boxShadow: neuShadowInset }}>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: '#15803d', marginBottom: 4, textTransform: 'uppercase' }}>
                                    Actions ({actions.length})
                                  </div>
                                  {actions.map((a, i) => (
                                    <div key={i} style={{ fontSize: 10, color: '#212529', marginBottom: 2 }}>
                                      {i + 1}. <strong>{(a.type || '').replace(/_/g, ' ')}</strong>
                                      {a.message && <span style={{ color: '#6C757D' }}> — "{a.message.substring(0, 40)}"</span>}
                                      {a.body && <span style={{ color: '#6C757D' }}> — "{a.body.substring(0, 40)}"</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Stats */}
                              <div style={{ fontSize: 10, color: '#ADB5BD', padding: '6px 0', borderTop: '1px solid #e9ecef', marginTop: 8, paddingTop: 8 }}>
                                Priority: <strong>{rule.priority}</strong> | Cooldown: <strong>{rule.cooldown_minutes}min</strong> | Fired: <strong>{rule.fire_count}x</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ========== SECTION 2: EXECUTION LOG ========== */}
      {automationLog.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: 0 }}>Execution Log (Last 50)</h4>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={expandAllLogs}
                style={{
                  boxShadow: neuShadowSm,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#FFFFFF',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#0369a1',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#e0f2fe'}
                onMouseOut={(e) => e.target.style.background = '#FFFFFF'}
              >
                Expand All
              </button>
              <button
                onClick={collapseAllLogs}
                style={{
                  boxShadow: neuShadowSm,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#FFFFFF',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#6C757D',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                onMouseOut={(e) => e.target.style.background = '#FFFFFF'}
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Search Logs */}
          <div style={{ marginBottom: 12, position: 'relative' }}>
            <input
              type="text"
              placeholder="Search logs by rule name or contact..."
              value={searchLogs}
              onChange={(e) => setSearchLogs(e.target.value)}
              style={{
                ...inp,
                width: '100%',
                paddingRight: 32
              }}
            />
            {searchLogs && (
              <button
                onClick={() => setSearchLogs('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: '#6C757D'
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {automationLog.length === 0 ? (
            <div style={emptyMsg}>No execution logs available.</div>
          ) : Object.values(groupedLogs).every(arr => arr.length === 0) && searchLogs ? (
            <div style={emptyMsg}>No logs match your search.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {['success', 'partial', 'failed'].map(result => {
                const logs = groupedLogs[result] || [];
                if (logs.length === 0) return null;

                const isExpanded = effectiveExpandedLogs.has(result);
                const color = resultColors[result] || '#6C757D';
                const resultLabel = result.charAt(0).toUpperCase() + result.slice(1);

                return (
                  <div key={result} style={{ marginBottom: 8 }}>
                    {/* Group Header */}
                    <div
                      onClick={() => toggleLogGroup(result)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 14px',
                        background: '#FFFFFF',
                        borderRadius: 10,
                        cursor: 'pointer',
                        borderLeft: `4px solid ${color}`,
                        boxShadow: neuShadowSm,
                        transition: 'all 0.2s',
                        userSelect: 'none'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#F8F9FA'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#FFFFFF'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ChevronRight
                          size={18}
                          color={color}
                          style={{
                            transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
                          {resultLabel}
                        </span>
                        <Badge text={logs.length} color={color} />
                      </div>
                    </div>

                    {/* Group Items */}
                    <div
                      style={{
                        maxHeight: isExpanded ? 'auto' : 0,
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease-in-out',
                        marginTop: isExpanded ? 8 : 0
                      }}
                    >
                      {logs.map((log, idx) => (
                        <div
                          key={log.id}
                          style={{
                            padding: '10px 12px',
                            borderRadius: 8,
                            background: idx % 2 === 0 ? '#FFFFFF' : '#F8F9FA',
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            marginBottom: 4,
                            boxShadow: neuShadowSm,
                            fontSize: 11
                          }}
                        >
                          <Badge text={log.result} color={resultColors[log.result] || '#6C757D'} />
                          <span style={{ fontWeight: 600, color: '#212529', flex: 1 }}>
                            {log.rule_name}
                          </span>
                          {(log.first_name || log.last_name) && (
                            log.contact_id ? (
                              <span onClick={() => onContactClick && onContactClick(log.contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                                {log.first_name} {log.last_name}
                              </span>
                            ) : (
                              <span style={{ color: '#6C757D' }}>
                                {log.first_name} {log.last_name}
                              </span>
                            )
                          )}
                          <span style={{ color: '#ADB5BD', whiteSpace: 'nowrap' }}>
                            {new Date(log.executed_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
